// =============================================
// COMPONENTE - GESTIÓN DE CUENTAS BANCARIAS
// Adaptado para Material-UI
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import bankReconciliationService from '../../services/bankReconciliationService';
import userService from '../../services/userService';
import bankService from '../../services/bankService';

const BankAccounts = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [FIAccounts, setFIAccounts] = useState([]);
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [banks, setBanks] = useState([]); // NUEVO: Catálogo de bancos
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    bank_name: '',
    account_number: '',
    account_type: '',
    account_holder: '',
    description: '',
    keywords: '',
    is_active: true
  });

  useEffect(() => {
    loadBanks(); // Cargar catálogo de bancos al inicio
    loadData();
  }, [activeTab]);

  const loadBanks = async () => {
    try {
      const response = await bankService.getActiveBanks();
      setBanks(response.banks || []);
    } catch (error) {
      console.error('Error al cargar bancos:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 0) {
        const response = await bankReconciliationService.getAllFIAccounts();
        setFIAccounts(response.accounts || []);
      } else {
        const [accountsResponse, usersResponse] = await Promise.all([
          bankReconciliationService.getAllOwnerAccounts(),
          userService.getAll()
        ]);
        setOwnerAccounts(accountsResponse.accounts || []);
        setUsers(usersResponse.users || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        user_id: account.user_id || '',
        bank_name: account.bank_name,
        account_number: account.account_number,
        account_type: account.account_type || '',
        account_holder: account.account_holder || '',
        description: account.description || '',
        keywords: account.keywords || '',
        is_active: account.is_active !== false
      });
    } else {
      setEditingAccount(null);
      setFormData({
        user_id: '',
        bank_name: '',
        account_number: '',
        account_type: '',
        account_holder: '',
        description: '',
        keywords: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      if (activeTab === 0) {
        const data = {
          bank_name: formData.bank_name,
          account_number: formData.account_number,
          account_type: formData.account_type,
          description: formData.description,
          is_active: formData.is_active
        };

        if (editingAccount) {
          await bankReconciliationService.updateFIAccount(editingAccount.id, data);
          setMessage({ type: 'success', text: 'Cuenta actualizada exitosamente' });
        } else {
          await bankReconciliationService.createFIAccount(data);
          setMessage({ type: 'success', text: 'Cuenta creada exitosamente' });
        }
      } else {
        const data = {
          user_id: formData.user_id,
          bank_name: formData.bank_name,
          account_number: formData.account_number,
          account_type: formData.account_type,
          account_holder: formData.account_holder,
          account_holder: formData.account_holder,
          keywords: formData.keywords,
          is_active: formData.is_active
        };

        if (editingAccount) {
          await bankReconciliationService.updateOwnerAccount(editingAccount.id, data);
          setMessage({ type: 'success', text: 'Cuenta actualizada exitosamente' });
        } else {
          await bankReconciliationService.createOwnerAccount(data);
          setMessage({ type: 'success', text: 'Cuenta creada exitosamente' });
        }
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al guardar cuenta'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cuenta?')) return;

    try {
      setLoading(true);
      if (activeTab === 0) {
        await bankReconciliationService.deleteFIAccount(accountId);
      } else {
        await bankReconciliationService.deleteOwnerAccount(accountId);
      }
      setMessage({ type: 'success', text: 'Cuenta eliminada exitosamente' });
      loadData();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al eliminar cuenta'
      });
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.name} - Casa ${user.house_number}` : 'Usuario desconocido';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            💳 Gestión de Cuentas Bancarias
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra las cuentas de la comunidad y de los propietarios
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Cuenta
        </Button>
      </Box>

      {/* Mensaje */}
      {message.text && (
        <Alert
          severity={message.type === 'success' ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="🏢 Cuentas de la Comunidad" />
          <Tab label="👥 Cuentas de Propietarios" />
        </Tabs>
      </Paper>

      {/* Tabla */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {activeTab === 1 && <TableCell>Propietario</TableCell>}
                  <TableCell>Banco</TableCell>
                  <TableCell>Número de Cuenta</TableCell>
                  <TableCell>Tipo</TableCell>
                  {activeTab === 0 ? (
                    <TableCell>Descripción</TableCell>
                  ) : (
                    <TableCell>Titular</TableCell>
                  )}
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(activeTab === 0 ? FIAccounts : ownerAccounts).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 6 : 7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No hay cuentas registradas
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (activeTab === 0 ? FIAccounts : ownerAccounts).map((account) => (
                    <TableRow key={account.id} hover>
                      {activeTab === 1 && (
                        <TableCell>{getUserName(account.user_id)}</TableCell>
                      )}
                      <TableCell>{account.bank_name}</TableCell>
                      <TableCell>{account.account_number}</TableCell>
                      <TableCell>{account.account_type || '-'}</TableCell>
                      <TableCell>
                        {activeTab === 0
                          ? (account.description || '-')
                          : (account.account_holder || '-')
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={account.is_active ? 'Activa' : 'Inactiva'}
                          color={account.is_active ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(account)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(account.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingAccount ? '✏️ Editar Cuenta' : '➕ Nueva Cuenta'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {activeTab === 1 && (
              <FormControl fullWidth>
                <InputLabel>Propietario *</InputLabel>
                <Select
                  value={formData.user_id}
                  label="Propietario *"
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                >
                  {users.filter(u => u.role === 'owner').map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} - Casa {user.house_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Banco *</InputLabel>
              <Select
                value={formData.bank_name}
                label="Banco *"
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              >
                <MenuItem value="">Seleccione un banco...</MenuItem>
                {banks.map(bank => (
                  <MenuItem key={bank.id} value={bank.bank_name}>
                    {bank.bank_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Número de Cuenta *"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              placeholder="Ej: 04-12-01-123456-7"
            />

            <FormControl fullWidth>
              <InputLabel>Tipo de Cuenta</InputLabel>
              <Select
                value={formData.account_type}
                label="Tipo de Cuenta"
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
              >
                <MenuItem value="">Selecciona...</MenuItem>
                <MenuItem value="Cuenta Corriente">Cuenta Corriente</MenuItem>
                <MenuItem value="Cuenta de Ahorros">Cuenta de Ahorros</MenuItem>
              </Select>
            </FormControl>

            {activeTab === 0 ? (
              <TextField
                fullWidth
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la cuenta"
              />
            ) : (
              <TextField
                fullWidth
                label="Titular de la Cuenta"
                value={formData.account_holder}
                onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                placeholder="Nombre del titular"
              />

            )}

            {activeTab === 1 && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Palabras Clave (Conciliación)"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="Ej: CASA 45, ROLY, Identificadores para conciliación automática"
                helperText="Separe con comas los términos de búsqueda"
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Cuenta activa"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.bank_name || !formData.account_number}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccounts;
